import type { IndexMarkSourceEnum } from './types';
import { UpdateActionEnum } from './types';

// Types in this file are generated from the Ava codebase, to provide accurate typing until OpenApi generation is complete

/**
 * Index message data item
 *
 * Note: The Timestamp field from the SBE message is not included in the data array.
 * The timestamp is available in the outer message's `ts` field instead.
 *
 */
export interface FixingIndex {
  /** Time of most recent update - ISO-8601 UTC string */
  Timestamp: string;
  /** Index ID - OID style ID */
  IndexID: string;
  /** Canonical symbol for this index */
  Name: string;
  /** Revision number */
  Revision: number;
  /** Display name for this index (empty in initial snapshot, set in updates) */
  DisplayName?: string;
  /** Update action */
  UpdateAction?: UpdateActionEnum;
  /** Update user ID - only present when source=User or on user updates */
  UpdateUserID?: number;
  /** The asset that the index tracks (semantic marshalling of AssetID)
   * - This is often a guaranteed value, drives the building of the index mapping for OTC
   */
  Asset?: string;
  /** The currency units of the index (semantic marshalling of QuoteCurrencyID)
   * - This is often a guaranteed value, drives the building of the index mapping for OTC
   */
  QuoteCurrency?: string;
  /** Whether this index is a price fixing or rate fixing index
   * Required as of Backend 2.55+
   */
  ValueType?: 'Price' | 'Rate';
  /** Default price increment - Display Field  */
  DefaultSpreadIncrement?: string;
  /** Canonical min increment for expressing the underlying price as a spread to the index  */
  MinSpreadIncrement?: string;
  /** Display spec for spread - Display Field */
  SpreadDisplaySpec?: string;
  /** Index fixing time - present when set */
  ValidNominalIndexTime?: number;
  /** Timezone definition that captures daylight savings on a given date */
  NominalIndexTimeZone?: string;
  /** Description of the index */
  Description?: string;
  /** The increment IndexMarks for this Index must satisfy to be applied as a fixing. */
  MinFixingIncrement?: string;
}

/**
 * Row class for consumers that need runtime class identity (e.g. `instanceof` checks in shared drawer logic).
 *
 * This class is a generic extension of the `FixingIndex` interface and can be reused by any implementer
 * that needs class-instance behavior while keeping interface-first typing.
 */
export class FixingIndexRow implements FixingIndex {
  Timestamp!: string;
  IndexID!: string;
  Name!: string;
  Revision!: number;
  DisplayName?: string;
  UpdateAction?: UpdateActionEnum;
  UpdateUserID?: number;
  Asset?: string;
  QuoteCurrency?: string;
  ValueType?: 'Price' | 'Rate';
  DefaultSpreadIncrement?: string;
  MinSpreadIncrement?: string;
  SpreadDisplaySpec?: string;
  ValidNominalIndexTime?: number;
  NominalIndexTimeZone?: string;
  Description?: string;
  MinFixingIncrement?: string;

  constructor(data: FixingIndex) {
    Object.assign(this, data);
  }
}

/** Combines the Index.Asset and Index.QuoteCurrency to build a Symbol */
export function getSymbolFromFixingIndex(fixingIndex: FixingIndex | undefined): string | undefined {
  if (!fixingIndex?.Asset || !fixingIndex.QuoteCurrency) {
    return undefined;
  }

  return `${fixingIndex.Asset}-${fixingIndex.QuoteCurrency}`;
}

/**
 * Index mark message data item.
 * (Note that IIndexMark from types.ts is inaccurate while both Index and IndexID are still present)
 *
 * Represents an individual observation/fixing for an index. Used by the Fixing Index Mark
 * WebSocket stream and REST API. Timestamps are ISO-8601 UTC strings (marshalled from
 * backend nanoseconds). Index is the canonical index name (FixingIndex.Name); IndexID is the OID.
 */
export interface FixingIndexMarkMessageData {
  /** Unique identifier of this mark entity (OID style ID, marshalled as string) */
  ID: string;
  /** Organization ID */
  OrgID: number;
  /** Time of most recent update - ISO-8601 UTC string */
  Timestamp: string;
  /** Revision number */
  Revision: number;
  /** Canonical index name (FixingIndex.Name), e.g. "XBX", "BTCUSD Index" - may be absent, use IndexID as fallback */
  Index: string;
  /** Index ID - OID style ID */
  IndexID: string;
  /** The time this observation is nominally as-of - ISO-8601 UTC string */
  NominalTime: string;
  /** The index level for this observation */
  Mark: string;
  /** Where this observation was sourced from */
  Source: IndexMarkSourceEnum;
  UpdateAction?: UpdateActionEnum;
  UpdateUser?: string;
}

/**
 * Row class for consumers that need runtime class identity (e.g. `instanceof` checks in shared drawer logic).
 *
 * This class is a generic extension of the `FixingIndexMarkMessageData` interface and can be reused by any implementer
 * that needs class-instance behavior while keeping interface-first typing.
 * Applies defaults for optional fields (UpdateAction, UpdateUser, Index) so consumers can rely on them being present.
 */
export class IndexMarkRow implements FixingIndexMarkMessageData {
  ID!: FixingIndexMarkMessageData['ID'];
  OrgID!: FixingIndexMarkMessageData['OrgID'];
  Timestamp!: FixingIndexMarkMessageData['Timestamp'];
  Revision!: FixingIndexMarkMessageData['Revision'];
  Index!: NonNullable<FixingIndexMarkMessageData['Index']>;
  IndexID!: FixingIndexMarkMessageData['IndexID'];
  NominalTime!: FixingIndexMarkMessageData['NominalTime'];
  Mark!: FixingIndexMarkMessageData['Mark'];
  Source!: FixingIndexMarkMessageData['Source'];
  UpdateAction!: NonNullable<FixingIndexMarkMessageData['UpdateAction']>;
  UpdateUser!: NonNullable<FixingIndexMarkMessageData['UpdateUser']>;

  constructor(data: FixingIndexMarkMessageData) {
    Object.assign(this, {
      ...data,
      UpdateAction: data.UpdateAction ?? UpdateActionEnum.Update,
      UpdateUser: data.UpdateUser ?? '',
    });
  }
}
